import React, { useEffect, useMemo, useState } from "react"
import moment from "moment"
import { Sparkles, Save } from "lucide-react"

import Button from "../../../components/ui/button/button"
import Checkbox from "../../../components/ui/checkbox/checkbox"
import Input from "../../../components/ui/inputs/input"
import Textarea from "../../../components/ui/inputs/textarea"
import Select from "../../../components/ui/select/select"

import { AI_MODELS } from "../registry"
import { loadToolConfig, parseJsonSafe, saveToolConfig } from "./storage"

/**
 * Generic per-tool configuration form. Each entry in `AI_TOOLS` renders one
 * instance of this — the parent page just passes the `tool` object through.
 *
 * Source of truth is Firestore (`ai_tooling/{tool.id}`). On mount the form
 * fetches the saved config; if none exists, it seeds from `tool.defaults`.
 * Edits stay local until Save is clicked, mirroring the rest of the admin
 * pages in the app (no autosave — too easy to accidentally clobber prod).
 *
 * Why JSON-as-textarea (vs. a structured field builder like Enquiry Ingest):
 * different tools have very different schema shapes (rate cards vs. stop sales
 * vs. classifier outputs), so the lowest-common-denominator authoring surface
 * is the raw schema. A schema builder can ride on top later for the tools
 * that benefit from one.
 */
function ToolConfigTab({ tool }) {
	const [loading, setLoading] = useState(true)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState(null)
	const [savedMeta, setSavedMeta] = useState(null)

	const [model, setModel] = useState(tool.defaults.model)
	const [temperature, setTemperature] = useState(String(tool.defaults.temperature))
	const [systemInstructions, setSystemInstructions] = useState(
		tool.defaults.system_instructions
	)
	const [userInstructions, setUserInstructions] = useState(
		tool.defaults.user_instructions
	)
	const [schemaText, setSchemaText] = useState(
		JSON.stringify(tool.defaults.json_schema, null, 2)
	)
	const [schemaName, setSchemaName] = useState(tool.defaults.json_schema_name)
	const [schemaStrict, setSchemaStrict] = useState(!!tool.defaults.json_schema_strict)

	useEffect(() => {
		let cancelled = false

		const init = async () => {
			setLoading(true)
			setError(null)
			try {
				const saved = await loadToolConfig(tool.id)
				if (cancelled) return

				if (saved) {
					setModel(saved.model ?? tool.defaults.model)
					setTemperature(String(saved.temperature ?? tool.defaults.temperature))
					setSystemInstructions(
						saved.system_instructions ?? tool.defaults.system_instructions
					)
					setUserInstructions(
						saved.user_instructions ?? tool.defaults.user_instructions
					)
					setSchemaText(
						JSON.stringify(
							saved.json_schema ?? tool.defaults.json_schema,
							null,
							2
						)
					)
					setSchemaName(saved.json_schema_name ?? tool.defaults.json_schema_name)
					setSchemaStrict(
						saved.json_schema_strict ?? !!tool.defaults.json_schema_strict
					)
					setSavedMeta({
						updated_at: saved.updated_at || null,
						updated_by: saved.updated_by || null,
					})
				} else {
					setSavedMeta(null)
				}
			} catch (err) {
				if (!cancelled) setError(err?.message || String(err))
			} finally {
				if (!cancelled) setLoading(false)
			}
		}

		init()
		return () => {
			cancelled = true
		}
		// `tool.defaults` is a module-level constant — keying the effect on
		// tool.id is enough to refetch on tab switch.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tool.id])

	const modelOptions = useMemo(
		() => Object.fromEntries(AI_MODELS.map((m) => [m, m])),
		[]
	)

	const schemaParse = useMemo(() => parseJsonSafe(schemaText), [schemaText])

	const save = async () => {
		setError(null)

		if (!schemaParse.ok) {
			setError(`JSON schema is not valid JSON - ${schemaParse.error}`)
			return
		}
		if (!schemaName?.trim()) {
			setError("JSON schema name is required")
			return
		}

		const tempNumber = Number(temperature)
		if (Number.isNaN(tempNumber)) {
			setError("Temperature must be a number")
			return
		}

		setSaving(true)
		try {
			await saveToolConfig(tool.id, {
				model,
				temperature: tempNumber,
				system_instructions: systemInstructions,
				user_instructions: userInstructions,
				json_schema: schemaParse.value,
				json_schema_name: schemaName.trim(),
				json_schema_strict: !!schemaStrict,
			})
			setSavedMeta({
				updated_at: { seconds: Math.floor(Date.now() / 1000) },
				updated_by: savedMeta?.updated_by || null,
			})
		} catch (err) {
			setError(err?.message || String(err))
		} finally {
			setSaving(false)
		}
	}

	const resetToDefaults = () => {
		setModel(tool.defaults.model)
		setTemperature(String(tool.defaults.temperature))
		setSystemInstructions(tool.defaults.system_instructions)
		setUserInstructions(tool.defaults.user_instructions)
		setSchemaText(JSON.stringify(tool.defaults.json_schema, null, 2))
		setSchemaName(tool.defaults.json_schema_name)
		setSchemaStrict(!!tool.defaults.json_schema_strict)
	}

	const lastSavedLabel = useMemo(() => {
		if (!savedMeta?.updated_at) return null
		const seconds =
			savedMeta.updated_at?.seconds ?? savedMeta.updated_at?._seconds ?? null
		if (!seconds) return null
		return moment.unix(seconds).format("D MMM YYYY [at] HH:mm")
	}, [savedMeta])

	if (loading) {
		return <div className="ai-tooling-loading">Loading {tool.label}...</div>
	}

	return (
		<div className="ai-tooling-tab">
			<div className="ai-tooling-tab-header">
				<div className="ai-tooling-tab-header-text">
					<h2>{tool.label}</h2>
					<p>{tool.description}</p>
					{lastSavedLabel && (
						<small>Last saved {lastSavedLabel}</small>
					)}
				</div>
				<div className="ai-tooling-tab-header-actions">
						<Button
							type="OUTLINE"
							label="Reset to defaults"
							onClick={resetToDefaults}
							disabled={saving}
						/>
					<Button
						label="Save"
						icon={<Save size={14} />}
						loading={saving}
						loadingText="Saving..."
						onClick={save}
						disabled={saving || !schemaParse.ok}
					/>
				</div>
			</div>

			{error && (
				<div
					className="ai-panel-error"
					role="alert">
					{error}
				</div>
			)}

			<section className="ai-panel is-open">
				<div className="ai-panel-toggle">
					<Sparkles size={14} />
					<span>Model</span>
				</div>
				<div className="ai-panel-body">
					<div className="ai-panel-grid">
						<div className="ai-panel-field">
							<Select
								showLabel
								placeholder="Model:"
								value={model}
								selected={model}
								onSelect={(option) => setModel(option.option)}
								options={modelOptions}
							/>
						</div>
						<div className="ai-panel-field">
							<Input
								type="text"
								placeholder="Temperature (0-2):"
								value={temperature}
								onChange={setTemperature}
								showLabel
							/>
						</div>
					</div>
				</div>
			</section>

			<section className="ai-panel is-open">
				<div className="ai-panel-toggle">
					<Sparkles size={14} />
					<span>System instructions</span>
				</div>
				<div className="ai-panel-body">
					<div className="ai-panel-field">
						<Textarea
							placeholder="System instructions"
							value={systemInstructions}
							onChange={setSystemInstructions}
							showLabel
							rows={18}
						/>
					</div>
				</div>
			</section>

			<section className="ai-panel is-open">
				<div className="ai-panel-toggle">
					<Sparkles size={14} />
					<span>User instructions</span>
				</div>
				<div className="ai-panel-body">
					<div className="ai-panel-field">
						<Textarea
							placeholder="User instructions"
							value={userInstructions}
							onChange={setUserInstructions}
							showLabel
							rows={14}
						/>
					</div>
				</div>
			</section>

			<section className="ai-panel is-open">
				<div className="ai-panel-toggle">
					<Sparkles size={14} />
					<span>JSON response schema</span>
				</div>
				<div className="ai-panel-body">
					<p className="ai-panel-description">
						Always returned as JSON. Paste the OpenAI Responses-API
						<code> json_schema</code> block - this is what the model is forced
						to produce.
					</p>
					<div className="ai-panel-grid">
						<div className="ai-panel-field">
							<Input
								type="text"
								placeholder="Schema name (snake_case):"
								value={schemaName}
								onChange={setSchemaName}
								showLabel
							/>
						</div>
						<div className="ai-panel-field ai-tooling-strict-toggle">
							<Checkbox
								label="Strict mode"
								checked={!!schemaStrict}
								onClick={() => setSchemaStrict((prev) => !prev)}
							/>
						</div>
					</div>
					<div className="ai-panel-field">
						<Textarea
							placeholder="JSON schema"
							value={schemaText}
							onChange={setSchemaText}
							showLabel
							rows={20}
							className="ai-tooling-schema-textarea"
						/>
						{!schemaParse.ok && (
							<small className="ai-tooling-schema-error">
								Invalid JSON - {schemaParse.error}
							</small>
						)}
					</div>
				</div>
			</section>
		</div>
	)
}

export default ToolConfigTab
